import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '../store'
import { resourceList } from '../admin/resources'
import partnerRoutes from './partnerRoutes'
import agentRoutes from './agentRoutes'
import { PARTNER_TOKEN_KEY } from '../services/partnerApi'
import { AGENT_TOKEN_KEY } from '../services/agentApi'

Vue.use(VueRouter)

// The partner portal is served on partners.logeco.africa. We detect it by host
// prefix (prod) or the /partner path (local dev) and, on that host, register ONLY
// the partner routes — admin links don't exist there (real isolation is
// still enforced server-side by the partner realm/scope).
// Decode a JWT's `exp` (no verification) to detect an expired admin session
// client-side, so a click redirects to login immediately instead of waiting for
// a page-level 401 that may never fire on a cached route.
function isJwtExpired(token) {
  try {
    // JWT payload is base64url — convert before atob (which only does base64).
    const part = (token.split('.')[1] || '').replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(atob(part))
    if (!payload || !payload.exp) return false
    return Date.now() >= payload.exp * 1000
  } catch (error) {
    // Can't decode → don't force a logout here; let server validation decide.
    return false
  }
}

function clearAdminSession() {
  localStorage.removeItem('authToken')
  localStorage.removeItem('isAuthenticated')
  store.commit('SET_ADMIN', null)
}

function isPartnerHost() {
  if (typeof window === 'undefined') return false
  if (process.env.VUE_APP_PORTAL === 'partner') return true
  const host = window.location.hostname || ''
  if (host.split('.')[0] === 'partners') return true
  return window.location.pathname.startsWith('/partner')
}

function isAgentHost() {
  if (typeof window === 'undefined') return false
  if (process.env.VUE_APP_PORTAL === 'agent') return true
  const host = window.location.hostname || ''
  if (host.split('.')[0] === 'agents') return true
  return window.location.pathname.startsWith('/agent')
}

function resourceRoutes(resource) {
  const meta = { requiresAuth: true, resourceKey: resource.key, superAdminOnly: Boolean(resource.superAdminOnly) }
  const list = { path: resource.path, name: `${resource.key}List`, component: () => import('../views/admin/resources/AdminResourceList.vue'), meta }
  const routes = [list]
  if (resource.key === 'notifications') {
    routes.push({ path: `${resource.path}/send`, name: 'notificationsSend', component: () => import('../views/admin/AdminNotificationsSend.vue'), meta })
  }
  if (['opportunities', 'schools', 'guides'].includes(resource.key)) {
    routes.push({ path: `${resource.path}/ingest`, name: `${resource.key}Ingest`, component: () => import('../views/admin/AdminIngest.vue'), meta: { ...meta, entity: resource.key } })
  }
  if (!resource.readOnly) {
    routes.push({ path: `${resource.path}/create`, name: `${resource.key}Create`, component: () => import('../views/admin/resources/AdminResourceFormPage.vue'), meta })
    routes.push({ path: `${resource.path}/:id/edit`, name: `${resource.key}Edit`, component: () => import('../views/admin/resources/AdminResourceFormPage.vue'), meta })
  }
  routes.push({ path: `${resource.path}/:id`, name: `${resource.key}Detail`, component: () => import('../views/admin/resources/AdminResourceDetail.vue'), meta })
  return routes
}

const routes = [
  { path: '/', redirect: '/app' },
  { path: '/login', name: 'Login', component: () => import('../views/Login.vue'), meta: { public: true } },
  {
    path: '/app',
    component: () => import('../views/admin/AdminLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', name: 'AdminDashboard', component: () => import('../views/admin/AdminDashboard.vue'), meta: { requiresAuth: true } },
      { path: 'agents', name: 'AdminAgents', component: () => import('../views/admin/AdminAgents.vue'), meta: { requiresAuth: true } },
      { path: 'agents/:id', name: 'AdminAgentDetail', component: () => import('../views/admin/AdminAgentDetail.vue'), meta: { requiresAuth: true } },
      { path: 'partners', name: 'AdminPartnersList', component: () => import('../views/admin/partners/AdminPartnersList.vue'), meta: { requiresAuth: true } },
      { path: 'partners/create', name: 'AdminPartnerCreate', component: () => import('../views/admin/partners/AdminPartnerCreate.vue'), meta: { requiresAuth: true } },
      { path: 'partners/:id/edit', name: 'AdminPartnerEdit', component: () => import('../views/admin/partners/AdminPartnerEdit.vue'), meta: { requiresAuth: true } },
      { path: 'partners/:id', name: 'AdminPartnerDetail', component: () => import('../views/admin/partners/AdminPartnerDetail.vue'), meta: { requiresAuth: true } },
      { path: 'partners/:id/members/create', name: 'AdminPartnerMemberCreate', component: () => import('../views/admin/partners/AdminPartnerMemberCreate.vue'), meta: { requiresAuth: true } },
      { path: 'partners/:id/members/:mid/password', name: 'AdminPartnerMemberPassword', component: () => import('../views/admin/partners/AdminPartnerMemberPassword.vue'), meta: { requiresAuth: true } },
      { path: 'partners/:id/sections/create', name: 'AdminPartnerSectionCreate', component: () => import('../views/admin/partners/AdminPartnerSectionEdit.vue'), meta: { requiresAuth: true } },
      { path: 'partners/:id/sections/:sid/edit', name: 'AdminPartnerSectionEdit', component: () => import('../views/admin/partners/AdminPartnerSectionEdit.vue'), meta: { requiresAuth: true } },
      ...resourceList.flatMap(resourceRoutes),
    ],
  },
  { path: '*', redirect: '/' },
]

const partnerMode = isPartnerHost()
const agentMode = !partnerMode && isAgentHost()
const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: partnerMode ? partnerRoutes : agentMode ? agentRoutes : routes,
})

router.beforeEach(async (to, from, next) => {
  // Partner portal guard (mobile-realm token in its own storage key).
  if (to.matched.some(record => record.meta.requiresPartnerAuth)) {
    const partnerToken = localStorage.getItem(PARTNER_TOKEN_KEY)
    if (!partnerToken) return next('/partner/login')
    return next()
  }
  if (to.matched.some(record => record.meta.partner)) return next()

  // Agent portal guard (own token storage key).
  if (to.matched.some(record => record.meta.requiresAgentAuth)) {
    const agentToken = localStorage.getItem(AGENT_TOKEN_KEY)
    if (!agentToken) return next('/agent/login')
    return next()
  }
  if (to.matched.some(record => record.meta.agent)) return next()

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  if (!requiresAuth) return next()
  const token = localStorage.getItem('authToken')
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true'
  // Absent or expired session → redirect to login right away (and clear the
  // stale token) instead of doing nothing until the next reload.
  if (!token || !isAuthenticated || isJwtExpired(token)) {
    clearAdminSession()
    return next('/login')
  }
  try {
    if (!store.state.admin) await store.dispatch('fetchAdminSession')
    if (to.matched.some(record => record.meta.superAdminOnly) && !store.getters.isSuperAdmin) return next('/app')
    return next()
  } catch (error) {
    // Only a genuine auth failure ends the session. Transient errors (network /
    // 5xx / timeout) keep the session and let the page surface its own error,
    // instead of bouncing to login (parity with the partner portal).
    if (error.response && error.response.status === 401) {
      clearAdminSession()
      return next('/login')
    }
    return next()
  }
})
export default router
